/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './SectionOurDNA.module.scss';

const PRINCIPLES = [
  {
    num: '01',
    label: 'Primary source or nothing',
    body: 'When a registry has not filed it, we say so, rather than passing off a guess as fact. What is sourced is marked sourced. What is modelled is marked modelled.',
    points: [
      'Sourced fields carry the registry they came from',
      'Modelled fields are labelled as modelled, never disguised',
      'A missing filing shows as a gap, not a guess',
    ],
  },
  {
    num: '02',
    label: 'Attribution travels with the data',
    body: 'Storage and engineering on every record we return. It would be cheaper to drop the source. We keep it.',
    points: [
      'Registry name, filing link and timestamp on every record',
      'Auditable end to end, no black box',
      'Provenance survives export and enrichment',
    ],
  },
  {
    num: '03',
    label: 'Coverage where the market stops',
    body: 'Registries that break weekly, and some that barely qualify as digital. We maintain them anyway.',
    points: [
      '400+ government registries, 200+ countries',
      'The markets most vendors leave blank',
      'Maintained through format changes and outages',
    ],
  },
  {
    num: '04',
    label: 'You own what you buy',
    body: 'Less revenue per seat than a metered contract. Reseller rights stay flexible.',
    points: [
      'Flexible reseller and redistribution rights',
      'No per-seat traps or usage clawbacks',
      'Build on the data and ship it',
    ],
  },
  {
    num: '05',
    label: 'Claims we can defend',
    body: "Deals lost to bigger numbers on someone else's slide.",
    points: [
      'Every figure on this page has a source behind it',
      'Coverage and limits published, not hidden',
      'Defensible in a procurement review',
    ],
  },
];

export const SectionOurDNA = () => {
  const dnaRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const el = dnaRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const total = r.height - window.innerHeight;
      if (total <= 0) {
        setActiveIdx(0);
        return;
      }
      const p = Math.max(0, Math.min(1, -r.top / total));
      setActiveIdx(Math.min(PRINCIPLES.length - 1, Math.floor(p * PRINCIPLES.length + 0.0001)));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Section background="#0c1930">
      <div className={styles.inner}>
        <div ref={dnaRef} className={styles.dna}>
          <div className={styles.sticky}>
            <div className={styles.head}>
              <div className={styles.rail}>
                <span className={styles.eyebrow}>04</span>
                <p className={styles.railTitle}>Our DNA</p>
              </div>
              <div className={styles.headContent}>
                <TitleBlock
                  title="Five things we will not trade away."
                  description="Each one costs us something, and the cost is written next to it. That is how you can tell they are real."
                  align="left"
                  theme="dark"
                  className={styles.titleBlock}
                />
              </div>
            </div>

            <div className={styles.dnaRow}>
              <nav className={styles.index} aria-label="Our principles">
                {PRINCIPLES.map((p, i) => (
                  <button
                    key={p.num}
                    className={cn(styles.navItem, activeIdx === i && styles.navItemActive)}
                    onClick={() => setActiveIdx(i)}
                  >
                    <span className={styles.navNum}>{p.num}</span>
                    {p.label}
                  </button>
                ))}
              </nav>

              <div className={styles.track}>
                {PRINCIPLES.map((p, i) => (
                  <article key={p.num} className={cn(styles.panel, activeIdx === i && styles.panelActive)}>
                    <div className={styles.panelLabel}>{p.label}</div>
                    <p className={styles.panelBody}>{p.body}</p>
                    <ul className={styles.points}>
                      {p.points.map((pt) => (
                        <li key={pt}>{pt}</li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};
